import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MetrcAuthDialog from '../../MetrcAuthDialog';
import MetrcSsoAuthDialog from '../../MetrcSsoAuthDialog';
import useMarkets from '../../hooks/useMarkets';
import { showToastError } from '../../components/ToastNotifications';
import { useLegacyAppSession } from '../../contexts/LegacyAppSessionContext';
import { loginToMarket } from '../../hooks/useMetrcImports';
import { getSsoCredentials } from '../../hooks/useSsoCredentials';
import type { SsoClientAuth, SsoCredentials } from '../../types/index';
import { beginKeycloakAuthorization } from '../../utils/keycloak-auth';
import { writeMetrcSsoRedirectState } from '../../utils/metrcSsoRedirectState';
import { useImportsWizard } from './ImportsWizardContext';
import { getSafeEndpointMessage } from '../../utils/requestErrors';

type SsoMarket = {
  id: number;
  code?: string;
  name?: string;
  ssoBaseUrl?: string | null;
  ssoClientId?: string | null;
  ssoClientAuth?: SsoClientAuth | null;
};

export default function MetrcAuthContainer() {
  const location = useLocation();
  const navigate = useNavigate();
  const { org } = useLegacyAppSession();
  const { data: markets } = useMarkets({
    organizationId: org?.id,
    enabled: Boolean(org?.id),
  });
  const {
    resetMetrcWizard,
    setMetrcAuthData,
    setMetrcFacilitiesSelected,
    setReturnTo,
    setWorkflow,
  } = useImportsWizard();
  const [ssoBusy, setSsoBusy] = useState(false);
  const [credentials, setCredentials] = useState<SsoCredentials | null>(null);
  const credentialsRequestIdRef = useRef(0);

  useEffect(() => {
    const nextReturnTo = typeof location.state?.returnTo === 'string'
      ? location.state.returnTo
      : '/imports';
    setReturnTo(nextReturnTo);
    setWorkflow(location.state?.workflow === 'products' || location.state?.workflow === 'facilities'
      ? location.state.workflow
      : 'integration');
  }, [location.state, setReturnTo, setWorkflow]);

  const handleClose = () => {
    const nextReturnTo = typeof location.state?.returnTo === 'string'
      ? location.state.returnTo
      : '/imports';
    resetMetrcWizard();
    navigate(nextReturnTo, { replace: true });
  };

  const handleSsoMarketChange = async (market: { id: number } | null) => {
    const requestId = ++credentialsRequestIdRef.current;
    setCredentials(null);
    if (!org?.id || !market?.id) return;

    try {
      const result = await getSsoCredentials({ organizationId: org.id, marketId: Number(market.id), purpose: 'import' });
      if (requestId !== credentialsRequestIdRef.current) return;
      setCredentials(result);
    } catch {
      // Non-critical - the credentials banner is informational only, so fail silently.
    }
  };

  const startSsoRedirect = async (market: SsoMarket, forceLogin: boolean) => {
    if (!org?.id) return;

    const marketId = Number(market.id);
    const marketLabel = market.code || market.name || '';
    if (!market.ssoBaseUrl || !market.ssoClientId) {
      showToastError('Selected market is missing SSO configuration.');
      return;
    }

    const nextReturnTo = typeof location.state?.returnTo === 'string'
      ? location.state.returnTo
      : '/imports';
    const nextWorkflow = location.state?.workflow === 'products' || location.state?.workflow === 'facilities'
      ? location.state.workflow
      : 'integration';

    writeMetrcSsoRedirectState({
      organizationId: org.id,
      marketId,
      market: marketLabel,
      ssoBaseUrl: market.ssoBaseUrl,
      ssoClientId: market.ssoClientId,
      ssoClientAuth: market.ssoClientAuth === 'confidential' ? 'confidential' : 'public',
      returnTo: nextReturnTo,
      workflow: nextWorkflow,
    });

    await beginKeycloakAuthorization({
      ssoBaseUrl: market.ssoBaseUrl,
      clientId: market.ssoClientId,
      redirectUri: `${window.location.origin}/imports/metrc/sso-callback`,
    }, { forceLogin });
  };

  const handleSsoContinue = async (market: SsoMarket) => {
    if (!org?.id) return;

    setSsoBusy(true);
    try {
      const marketId = Number(market.id);
      const marketLabel = market.code || market.name || '';
      const result = await getSsoCredentials({ organizationId: org.id, marketId, purpose: 'import' });

      // The server decides whether a stored sign-in can be used - trust it over inferring from the client.
      if (result?.requiresSsoAuthentication === false) {
        setMetrcAuthData({ market: marketLabel, marketId });
        setMetrcFacilitiesSelected([]);
        navigate('/imports/metrc/select');
        return;
      }

      await startSsoRedirect(market, false);
    } catch (error) {
      console.error(error);
      showToastError(getSafeEndpointMessage(error, 'Error trying to authenticate'));
    } finally {
      setSsoBusy(false);
    }
  };

  // Skips the stored sign-in: the user signs in with their own MCA account, and only their own sign-in is saved.
  const handleUseOwnCredentials = async (market: SsoMarket) => {
    setSsoBusy(true);
    try {
      await startSsoRedirect(market, true);
    } catch (error) {
      console.error(error);
      showToastError(getSafeEndpointMessage(error, 'Error trying to authenticate'));
    } finally {
      setSsoBusy(false);
    }
  };

  if (org?.ssoEnabled) {
    return (
      <MetrcSsoAuthDialog
        markets={markets}
        busy={ssoBusy}
        credentials={credentials}
        onClose={handleClose}
        onContinue={handleSsoContinue}
        onUseOwnCredentials={handleUseOwnCredentials}
        onMarketChange={handleSsoMarketChange}
      />
    );
  }

  return (
    <MetrcAuthDialog
      markets={markets}
      onClose={handleClose}
      onAuthenticate={async ({ market, username, password }) => {
        if (!org?.id) return;

        try {
          const selectedMarket = (markets || []).find((item) =>
            item.code === market || item.name === market
          );
          const marketId = Number(selectedMarket?.id || 0);

          if (!marketId) {
            showToastError('Selected market could not be mapped.');
            return;
          }

          const result = await loginToMarket({ marketId, username, password });
          if (result?.error) {
            showToastError(getSafeEndpointMessage(result, 'Error trying to login'));
            return;
          }

          setMetrcAuthData({ market, marketId, username });
          setMetrcFacilitiesSelected([]);
          navigate('/imports/metrc/select');
        } catch (error) {
          console.error(error);
          showToastError(getSafeEndpointMessage(error, 'Error trying to login'));
        }
      }}
    />
  );
}

